import { GetTransactionsResponse, Payment } from "../../../types/transactions";
import {
  computeTransactionTotals,
  lineItemChargeInclTax,
  visibleRefunds,
} from "../../../utils/transactionTotals";
import { formatPaymentMethod } from "../../../utils/paymentMethodFormatters";

/** Full client name, or an em dash when unknown (shared with the receipt). */
export { clientName } from "../../../utils/receiptModel";

/** Pseudo-tenders that carry no cash and are never shown as purchases. */
export function isCashTender(p: Payment): boolean {
  return (
    p.type !== "loyalty_redemption" &&
    p.type !== "package_redemption" &&
    p.method !== "Loyalty"
  );
}

/** Real captured tenders on a transaction (succeeded, cash-bearing, non-refund).
 *  Drives cash money math — the Captured/Net summary and the Transactions
 *  Method column — so it stays cash-only. */
export function capturedTenders(row: GetTransactionsResponse): Payment[] {
  return (row.payments || []).filter(
    (p) => p.status === "succeeded" && p.type !== "refund" && isCashTender(p),
  );
}

/** Every tender shown as a row in the Payments list — includes the
 *  loyalty/package redemption pseudo-tenders (surfaced as their own rows, but
 *  kept out of the cash summary totals via isCashTender). Succeeded, non-refund. */
export function listTenders(row: GetTransactionsResponse): Payment[] {
  return (row.payments || []).filter(
    (p) => p.status === "succeeded" && p.type !== "refund",
  );
}

/** Money returned on a transaction (visible refunds sum). */
export function refundedTotal(row: GetTransactionsResponse): number {
  return visibleRefunds(row).reduce((sum, r) => sum + r.amount, 0);
}

/** Order total after redemptions (what the sale came to). */
export function grossTotal(row: GetTransactionsResponse): number {
  return computeTransactionTotals(row).totalAfterRedemptions;
}

/** Net of the sale = gross − refunded. */
export function netTotal(row: GetTransactionsResponse): number {
  return grossTotal(row) - refundedTotal(row);
}

/** Count of line items across services / products / packages / gift cards. */
export function itemCount(row: GetTransactionsResponse): number {
  const li = row.line_items;
  return (
    (li?.booked_services?.length ?? 0) +
    (li?.products?.length ?? 0) +
    (li?.service_packages?.length ?? 0) +
    (li?.gift_cards?.length ?? 0)
  );
}

/** Comma-separated line-item names (services, products, packages, gift cards)
 *  for the Transactions list cell (truncated with ellipsis by the cell). */
export function lineItemNames(row: GetTransactionsResponse): string {
  const li = row.line_items;
  const names = [
    ...(li?.booked_services ?? []).map((s) => s.name),
    ...(li?.products ?? []).map((p) => p.name),
    ...(li?.service_packages ?? []).map((pkg) => pkg.name),
    ...(li?.gift_cards ?? []).map((gc) => gc.name),
  ].filter(Boolean);
  return names.join(", ");
}

/**
 * Payment column label for the Transactions table: one method, `{n} methods`,
 * or `—`. When there is a single card tender carrying a last4, it renders as
 * `{brand||method} ••1234`; the multi-method case stays a plain count.
 */
export function paymentColumnLabel(row: GetTransactionsResponse): string {
  const tenders = capturedTenders(row);
  const methods = Array.from(
    new Set(tenders.map((p) => formatPaymentMethod(p.method))),
  ).filter(Boolean);
  if (methods.length === 0) return "—";
  if (methods.length === 1) {
    // A single distinct method: if exactly one tender carries a last4, show it.
    const withLast4 = tenders.filter((p) => p.card_last4);
    if (withLast4.length === 1) {
      const p = withLast4[0];
      const label =
        p.card_brand && p.card_brand.trim().length > 0
          ? p.card_brand
          : methods[0];
      return `${label} ••${p.card_last4}`;
    }
    return methods[0];
  }
  return `${methods.length} methods`;
}

/**
 * APPLIED TO derivation (override #4). We have no payment→line mapping, so a
 * `deposit`-type payment is "Deposit" and everything else is "Balance".
 */
export function appliedToLabel(payment: Payment): "Deposit" | "Balance" {
  return payment.type === "deposit" ? "Deposit" : "Balance";
}

/** Flattened read-only line items for a detail panel. */
export interface DetailLineItem {
  key: string;
  name: string;
  staff?: string;
  amount: number;
  /** Product quantity (>1) so the row can show the multiplication into total. */
  qty?: number;
  unitPrice?: number;
}

export function detailLineItems(
  row: GetTransactionsResponse,
): DetailLineItem[] {
  const out: DetailLineItem[] = [];
  (row.line_items?.booked_services ?? []).forEach((s) =>
    out.push({
      key: `svc-${s.id}`,
      name: s.name,
      staff: s.staff_name || undefined,
      amount: lineItemChargeInclTax(s),
    }),
  );
  (row.line_items?.products ?? []).forEach((p) =>
    out.push({
      key: `prod-${p.id}`,
      name: p.name,
      amount: lineItemChargeInclTax(p),
      qty: p.quantity ?? 1,
      unitPrice: p.unit_price,
    }),
  );
  (row.line_items?.service_packages ?? []).forEach((pkg) =>
    out.push({
      key: `pkg-${pkg.line_item_id}`,
      name: pkg.name,
      amount: lineItemChargeInclTax(pkg),
    }),
  );
  // Gift-card sale lines are tax-exempt (face value = charge), so use the
  // amount directly — mirrors computeTransactionTotals' giftCardTotal.
  (row.line_items?.gift_cards ?? []).forEach((gc) =>
    out.push({
      key: `gift-${gc.line_item_id}`,
      name: gc.name,
      amount: gc.amount || 0,
    }),
  );
  return out;
}

/** True when the Void action applies: an unpaid quick sale (no appointment
 *  status) or an unpaid Cancelled / No Show appointment, with nothing
 *  refunded. The backend enforces
 *  the same rule plus the card block. */
export function canVoidTransaction(
  row: Pick<GetTransactionsResponse, "transaction">,
): boolean {
  const txn = row.transaction;
  if (txn?.transaction_status !== "unpaid") return false;
  if (txn.refund_status && txn.refund_status !== "none") return false;
  const appt = txn.appointment_status;
  return appt == null || appt === "Cancelled" || appt === "No Show";
}
